/**
 * JourneyPDFExport - CA-Ready PDF Export for Journey Plan
 *
 * Generates a printable/exportable PDF including:
 * - User profile summary
 * - Timeline visualization
 * - Tax savings breakdown
 * - Scenario comparison
 * - Liquidation sequence
 * - CA consultation checklist
 * - Action items with deadlines
 * - Assumptions and disclaimers
 *
 * Uses browser print functionality for PDF generation.
 */

import { useRef, forwardRef } from 'react';
import { cn } from '@/lib/utils';
import type { JourneyOutput, ScenarioComparison } from '../../../api/_schemas/journeyOutput.schema';

// Types for the PDF content
interface UserProfile {
  name?: string;
  currentCountry?: string;
  targetReturnDate?: string;
  totalAssetsUSD?: number;
  totalAssetsINR?: number;
  monthlyExpenseINR?: number;
}

interface LiquidationItem {
  id: string;
  sequence: number;
  asset: string;
  action: string;
  timing: 'before_move' | 'during_rnor' | 'post_rnor';
  timingDescription: string;
  reason: string;
  estimatedValueUSD: number;
  estimatedValueINR: number;
  taxImplication: string;
  requiresCAReview: boolean;
}

interface JourneyPDFExportProps {
  journeyData: JourneyOutput;
  userProfile: UserProfile;
  liquidationItems?: LiquidationItem[];
  scenarioComparison?: ScenarioComparison;
  onClose?: () => void;
  className?: string;
}

// Format helpers
function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatINR(amount: number): string {
  if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)} L`;
  return `₹${amount.toLocaleString('en-IN')}`;
}

function formatUSD(amount: number): string {
  if (amount >= 1000000) return `$${(amount / 1000000).toFixed(2)}M`;
  if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}K`;
  return `$${amount.toLocaleString('en-US')}`;
}

// Print-optimized content component
const PDFContent = forwardRef<HTMLDivElement, JourneyPDFExportProps>(
  ({ journeyData, userProfile, liquidationItems, scenarioComparison }, ref) => {
    const generatedDate = new Date().toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });

    return (
      <div
        ref={ref}
        className="bg-white p-8 max-w-4xl mx-auto print:p-4 print:max-w-none"
        style={{ fontFamily: 'system-ui, sans-serif' }}
      >
        {/* Header */}
        <div className="border-b-2 border-emerald-500 pb-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-800">
                Personalized Return Journey Plan
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                Generated by NRI PathFinder • {generatedDate}
              </p>
            </div>
            <div className="text-right">
              <div className="text-xs text-slate-400">CONFIDENTIAL</div>
              <div className="text-xs text-slate-400">For CA Review</div>
            </div>
          </div>
        </div>

        {/* Profile Summary */}
        <section className="mb-6 print:break-inside-avoid">
          <h2 className="text-lg font-semibold text-slate-800 border-b border-slate-200 pb-2 mb-3">
            1. Profile Summary
          </h2>
          <div className="grid grid-cols-2 gap-4 text-sm">
            {userProfile.name && (
              <div>
                <span className="text-slate-500">Name:</span>{' '}
                <span className="font-medium">{userProfile.name}</span>
              </div>
            )}
            <div>
              <span className="text-slate-500">Current Country:</span>{' '}
              <span className="font-medium">
                {userProfile.currentCountry || 'USA'}
              </span>
            </div>
            <div>
              <span className="text-slate-500">Target Return Date:</span>{' '}
              <span className="font-medium">
                {userProfile.targetReturnDate
                  ? formatDate(userProfile.targetReturnDate)
                  : journeyData.timeline.targetReturnDate
                    ? formatDate(journeyData.timeline.targetReturnDate)
                    : 'TBD'}
              </span>
            </div>
            <div>
              <span className="text-slate-500">Financial Year:</span>{' '}
              <span className="font-medium">
                {journeyData.timeline.targetReturnFY || 'TBD'}
              </span>
            </div>
            {userProfile.totalAssetsUSD && (
              <div>
                <span className="text-slate-500">Total Assets (USD):</span>{' '}
                <span className="font-medium">
                  {formatUSD(userProfile.totalAssetsUSD)}
                </span>
              </div>
            )}
            {userProfile.totalAssetsINR && (
              <div>
                <span className="text-slate-500">Total Assets (INR):</span>{' '}
                <span className="font-medium">
                  {formatINR(userProfile.totalAssetsINR)}
                </span>
              </div>
            )}
          </div>
        </section>

        {/* Hero Stats */}
        {journeyData.heroStats && (
          <section className="mb-6 print:break-inside-avoid">
            <h2 className="text-lg font-semibold text-slate-800 border-b border-slate-200 pb-2 mb-3">
              2. Key Metrics
            </h2>
            <div className="grid grid-cols-4 gap-3">
              <div className="bg-emerald-50 rounded-lg p-3 text-center border border-emerald-200">
                <div className="text-xl font-bold text-emerald-700">
                  {journeyData.heroStats.totalTaxSavings.formatted}
                </div>
                <div className="text-xs text-slate-600">Tax Savings</div>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 text-center border border-blue-200">
                <div className="text-xl font-bold text-blue-700">
                  {journeyData.heroStats.rnorBenefit.years} Years
                </div>
                <div className="text-xs text-slate-600">RNOR Window</div>
              </div>
              <div className="bg-purple-50 rounded-lg p-3 text-center border border-purple-200">
                <div className="text-xl font-bold text-purple-700">
                  {journeyData.heroStats.confidence.score}%
                </div>
                <div className="text-xs text-slate-600">Confidence</div>
              </div>
              <div className="bg-amber-50 rounded-lg p-3 text-center border border-amber-200">
                <div className="text-xl font-bold text-amber-700">
                  {journeyData.heroStats.timeToFIRE.formatted}
                </div>
                <div className="text-xs text-slate-600">Time to FIRE</div>
              </div>
            </div>
          </section>
        )}

        {/* Tax Savings Breakdown */}
        {journeyData.taxSavingsHighlight && (
          <section className="mb-6 print:break-inside-avoid">
            <h2 className="text-lg font-semibold text-slate-800 border-b border-slate-200 pb-2 mb-3">
              3. Tax Savings Breakdown
            </h2>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50">
                  <th className="text-left py-2 px-3 border">Category</th>
                  <th className="text-right py-2 px-3 border">Amount</th>
                  <th className="text-left py-2 px-3 border">Timing</th>
                  <th className="text-center py-2 px-3 border">Confidence</th>
                </tr>
              </thead>
              <tbody>
                {journeyData.taxSavingsHighlight.savingsBreakdown.map(
                  (item, idx) => (
                    <tr key={idx}>
                      <td className="py-2 px-3 border">
                        <div className="font-medium">{item.category}</div>
                        <div className="text-xs text-slate-500">
                          {item.description}
                        </div>
                      </td>
                      <td className="text-right py-2 px-3 border font-medium">
                        {item.formatted}
                      </td>
                      <td className="py-2 px-3 border text-slate-600">
                        {item.timing}
                      </td>
                      <td className="text-center py-2 px-3 border">
                        <span
                          className={cn(
                            'px-2 py-0.5 rounded text-xs',
                            item.confidence === 'high' &&
                              'bg-emerald-100 text-emerald-700',
                            item.confidence === 'medium' &&
                              'bg-amber-100 text-amber-700',
                            item.confidence === 'estimated' &&
                              'bg-slate-100 text-slate-600'
                          )}
                        >
                          {item.confidence}
                        </span>
                      </td>
                    </tr>
                  )
                )}
              </tbody>
              <tfoot>
                <tr className="bg-emerald-50 font-semibold">
                  <td className="py-2 px-3 border">TOTAL</td>
                  <td className="text-right py-2 px-3 border text-emerald-700">
                    {journeyData.taxSavingsHighlight.totalFormatted}
                  </td>
                  <td className="py-2 px-3 border" colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
            <p className="text-xs text-slate-500 mt-2 italic">
              {journeyData.taxSavingsHighlight.methodology}
            </p>
          </section>
        )}

        {/* Scenario Comparison */}
        {scenarioComparison && scenarioComparison.scenarios.length > 0 && (
          <section className="mb-6 print:break-inside-avoid print:break-before-page">
            <h2 className="text-lg font-semibold text-slate-800 border-b border-slate-200 pb-2 mb-3">
              4. Return Timing Scenarios
            </h2>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50">
                  <th className="text-left py-2 px-3 border">Scenario</th>
                  <th className="text-center py-2 px-3 border">Return Date</th>
                  <th className="text-right py-2 px-3 border">Tax Savings</th>
                  <th className="text-center py-2 px-3 border">RNOR</th>
                  <th className="text-center py-2 px-3 border">Risk</th>
                </tr>
              </thead>
              <tbody>
                {scenarioComparison.scenarios.map((scenario) => (
                  <tr
                    key={scenario.id}
                    className={cn(scenario.isRecommended && 'bg-emerald-50')}
                  >
                    <td className="py-2 px-3 border">
                      <div className="font-medium">
                        {scenario.isRecommended && '✓ '}
                        {scenario.name}
                      </div>
                      {scenario.isCurrentPlan && (
                        <span className="text-xs text-blue-600">Your Plan</span>
                      )}
                    </td>
                    <td className="text-center py-2 px-3 border">
                      {scenario.returnDate}
                    </td>
                    <td className="text-right py-2 px-3 border font-medium">
                      {scenario.metrics.taxSavingsFormatted}
                    </td>
                    <td className="text-center py-2 px-3 border">
                      {scenario.metrics.rnorYears} yrs
                    </td>
                    <td className="text-center py-2 px-3 border">
                      <span
                        className={cn(
                          'px-2 py-0.5 rounded text-xs',
                          scenario.metrics.riskLevel === 'low' &&
                            'bg-emerald-100 text-emerald-700',
                          scenario.metrics.riskLevel === 'medium' &&
                            'bg-amber-100 text-amber-700',
                          scenario.metrics.riskLevel === 'high' &&
                            'bg-red-100 text-red-700'
                        )}
                      >
                        {scenario.metrics.riskLevel}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-3 p-3 bg-slate-50 rounded-lg text-sm">
              <div className="font-medium text-slate-700">Recommendation:</div>
              <p className="text-slate-600">
                {scenarioComparison.recommendationRationale}
              </p>
              <div className="mt-2 font-medium text-slate-700">Key Trade-off:</div>
              <p className="text-slate-600">{scenarioComparison.keyTradeoff}</p>
            </div>
          </section>
        )}

        {/* Liquidation Sequence */}
        {liquidationItems && liquidationItems.length > 0 && (
          <section className="mb-6 print:break-inside-avoid">
            <h2 className="text-lg font-semibold text-slate-800 border-b border-slate-200 pb-2 mb-3">
              5. Asset Liquidation Sequence
            </h2>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50">
                  <th className="text-center py-2 px-2 border w-10">#</th>
                  <th className="text-left py-2 px-3 border">Asset</th>
                  <th className="text-left py-2 px-3 border">Action</th>
                  <th className="text-left py-2 px-3 border">Timing</th>
                  <th className="text-right py-2 px-3 border">Value</th>
                  <th className="text-center py-2 px-3 border">CA?</th>
                </tr>
              </thead>
              <tbody>
                {liquidationItems.map((item) => (
                  <tr
                    key={item.id}
                    className={cn(
                      item.timing === 'before_move' && 'bg-blue-50',
                      item.timing === 'during_rnor' && 'bg-emerald-50'
                    )}
                  >
                    <td className="text-center py-2 px-2 border font-bold">
                      {item.sequence}
                    </td>
                    <td className="py-2 px-3 border font-medium">{item.asset}</td>
                    <td className="py-2 px-3 border">{item.action}</td>
                    <td className="py-2 px-3 border">
                      <div className="font-medium">{item.timingDescription}</div>
                      <div className="text-xs text-slate-500">
                        {item.timing === 'before_move' && '⚠️ Before Return'}
                        {item.timing === 'during_rnor' && '🛡️ During RNOR'}
                        {item.timing === 'post_rnor' && '📅 Post-RNOR'}
                      </div>
                    </td>
                    <td className="text-right py-2 px-3 border">
                      <div>{formatUSD(item.estimatedValueUSD)}</div>
                      <div className="text-xs text-slate-500">
                        {formatINR(item.estimatedValueINR)}
                      </div>
                    </td>
                    <td className="text-center py-2 px-3 border">
                      {item.requiresCAReview ? (
                        <span className="text-amber-600 font-medium">Yes</span>
                      ) : (
                        <span className="text-slate-400">No</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {/* Action Items by Phase */}
        <section className="mb-6 print:break-before-page">
          <h2 className="text-lg font-semibold text-slate-800 border-b border-slate-200 pb-2 mb-3">
            6. Action Items by Phase
          </h2>
          {journeyData.phases.map((phase) => (
            <div key={phase.id} className="mb-4 print:break-inside-avoid">
              <h3 className="font-semibold text-slate-700 bg-slate-50 px-3 py-2 rounded-t border border-b-0">
                {phase.name}
                <span className="text-xs font-normal text-slate-500 ml-2">
                  {phase.dateRange.start} → {phase.dateRange.end}
                </span>
              </h3>
              <div className="border border-t-0 rounded-b">
                {phase.actions.map((action, idx) => (
                  <div
                    key={action.id}
                    className={cn(
                      'px-3 py-2 text-sm',
                      idx !== phase.actions.length - 1 && 'border-b'
                    )}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="font-medium text-slate-800">
                          {action.sequence}. {action.title}
                          {action.priority === 'critical' && (
                            <span className="ml-2 px-1.5 py-0.5 bg-red-100 text-red-700 text-xs rounded">
                              CRITICAL
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-600 mt-0.5">
                          {action.description}
                        </div>
                      </div>
                      <div className="text-right text-xs text-slate-500 ml-4">
                        <div>{action.suggestedTiming}</div>
                        {action.requiresCAReview && (
                          <div className="text-amber-600">CA Review</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </section>

        {/* Key Decision Points */}
        {journeyData.keyDecisionPoints.length > 0 && (
          <section className="mb-6 print:break-inside-avoid">
            <h2 className="text-lg font-semibold text-slate-800 border-b border-slate-200 pb-2 mb-3">
              7. Key Decision Points (Requires CA Review)
            </h2>
            <div className="space-y-3">
              {journeyData.keyDecisionPoints.map((decision) => (
                <div
                  key={decision.id}
                  className="p-3 border rounded-lg bg-amber-50 border-amber-200"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-medium text-slate-800">
                        {decision.title}
                      </div>
                      <div className="text-sm text-slate-600 mt-1">
                        {decision.description}
                      </div>
                    </div>
                    <div className="text-xs text-slate-500">
                      Decide by: {decision.suggestedDecisionDate}
                    </div>
                  </div>
                  {decision.impactIfDelayed && (
                    <div className="mt-2 text-xs text-red-600">
                      ⚠️ If delayed: {decision.impactIfDelayed}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Assumptions */}
        <section className="mb-6 print:break-inside-avoid">
          <h2 className="text-lg font-semibold text-slate-800 border-b border-slate-200 pb-2 mb-3">
            8. Assumptions Used
          </h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50">
                <th className="text-left py-2 px-3 border">Category</th>
                <th className="text-left py-2 px-3 border">Assumption</th>
                <th className="text-center py-2 px-3 border">Source</th>
              </tr>
            </thead>
            <tbody>
              {journeyData.assumptions.map((assumption, idx) => (
                <tr key={idx}>
                  <td className="py-2 px-3 border font-medium">
                    {assumption.category}
                  </td>
                  <td className="py-2 px-3 border">{assumption.assumption}</td>
                  <td className="text-center py-2 px-3 border">
                    <span
                      className={cn(
                        'px-2 py-0.5 rounded text-xs',
                        assumption.source === 'user_input' &&
                          'bg-blue-100 text-blue-700',
                        assumption.source === 'calculated' &&
                          'bg-purple-100 text-purple-700',
                        assumption.source === 'system_default' &&
                          'bg-slate-100 text-slate-600'
                      )}
                    >
                      {assumption.source.replace('_', ' ')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* Disclaimers */}
        <section className="mb-6 print:break-inside-avoid">
          <h2 className="text-lg font-semibold text-slate-800 border-b border-slate-200 pb-2 mb-3">
            9. Important Disclaimers
          </h2>
          <div className="bg-slate-50 p-4 rounded-lg text-sm space-y-3">
            <p className="text-slate-700">{journeyData.disclaimers.standard}</p>

            {journeyData.disclaimers.itemsRequiringCAReview.length > 0 && (
              <div>
                <div className="font-medium text-amber-700 mb-1">
                  Items Requiring CA Review:
                </div>
                <ul className="list-disc list-inside text-slate-600">
                  {journeyData.disclaimers.itemsRequiringCAReview.map(
                    (item, idx) => (
                      <li key={idx}>{item}</li>
                    )
                  )}
                </ul>
              </div>
            )}

            {journeyData.disclaimers.dataLimitations.length > 0 && (
              <div>
                <div className="font-medium text-slate-700 mb-1">
                  Data Limitations:
                </div>
                <ul className="list-disc list-inside text-slate-600">
                  {journeyData.disclaimers.dataLimitations.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t-2 border-slate-200 pt-4 mt-8 text-center text-xs text-slate-500">
          <p>
            Generated by{' '}
            <span className="font-semibold text-emerald-600">NRI PathFinder</span> •
            nripathfinder.com
          </p>
          <p className="mt-1">
            This document is for informational purposes only and does not constitute
            financial or tax advice.
          </p>
          <p className="mt-1">
            Please consult a qualified Chartered Accountant before making any
            financial decisions.
          </p>
          <p className="mt-2 text-slate-400">
            Document ID: {journeyData.generatedAt.replace(/[:.]/g, '-')}
          </p>
        </footer>
      </div>
    );
  }
);

PDFContent.displayName = 'PDFContent';

// Main export component with print controls
export function JourneyPDFExport(props: JourneyPDFExportProps) {
  const contentRef = useRef<HTMLDivElement>(null);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className={cn('relative', props.className)}>
      {/* Print Controls - Hidden when printing */}
      <div className="print:hidden sticky top-0 bg-white z-10 p-4 border-b shadow-sm">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-slate-800">
              Journey Plan - PDF Preview
            </h2>
            <p className="text-sm text-slate-500">
              Review before printing or saving as PDF
            </p>
          </div>
          <div className="flex items-center gap-3">
            {props.onClose && (
              <button
                onClick={props.onClose}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 transition-colors"
              >
                Cancel
              </button>
            )}
            <button
              onClick={handlePrint}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors flex items-center gap-2"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                />
              </svg>
              Print / Save PDF
            </button>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            .print-content, .print-content * {
              visibility: visible;
            }
            .print-content {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
            }
            @page {
              margin: 1cm;
              size: A4;
            }
          }
        `}
      </style>

      {/* PDF Content */}
      <div className="print-content">
        <PDFContent ref={contentRef} {...props} />
      </div>
    </div>
  );
}

export default JourneyPDFExport;
